/** Admin API hooks: list/create/revoke access links. Creating a link returns its raw token + URL +
 *  QR once (the server only stores the hash). See docs/auth-and-access.md. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./client";

export interface AccessLinkSummary {
  id: string;
  label: string;
  expiresAt: string;
  maxUses: number | null;
  usedCount: number;
  revokedAt: string | null;
  createdAt: string;
  status: "active" | "expired" | "revoked" | "exhausted";
  /** The full access URL, retained so active links can be re-copied; null for legacy/pre-feature links. */
  url: string | null;
}

export interface CreatedLink {
  id: string;
  label: string;
  url: string;
  token: string;
  qrDataUrl: string;
  expiresAt: string;
  maxUses: number | null;
}

const LINKS_KEY = ["admin", "links"] as const;

export function useLinks(enabled: boolean) {
  return useQuery({
    queryKey: LINKS_KEY,
    queryFn: () => apiFetch<{ links: AccessLinkSummary[] }>("/api/admin/links").then((r) => r.links),
    enabled,
  });
}

export function useCreateLink() {
  const qc = useQueryClient();
  return useMutation({
    // expiresInHours: null = no expiry (unlimited).
    mutationFn: (input: { label: string; expiresInHours: number | null; maxUses?: number }) =>
      apiFetch<CreatedLink>("/api/admin/links", { method: "POST", body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: LINKS_KEY }),
  });
}

/** A link created with "no expiry" is stored with a far-future sentinel date; detect it by year. */
export function linkNeverExpires(expiresAt: string): boolean {
  return new Date(expiresAt).getFullYear() >= 9000;
}

export function useRevokeLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<{ ok: true }>(`/api/admin/links/${id}/revoke`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: LINKS_KEY }),
  });
}

/** One access link's shareable detail (URL + QR) for the standalone share view. */
export interface AdminLinkDetail {
  id: string;
  label: string;
  url: string | null;
  qrDataUrl: string | null;
  expiresAt: string;
  maxUses: number | null;
  usedCount: number;
  status: AccessLinkSummary["status"];
}

export function useAdminLink(id: string | null) {
  return useQuery({
    queryKey: ["admin", "link", id],
    enabled: !!id,
    queryFn: () => apiFetch<AdminLinkDetail>(`/api/admin/links/${id}`),
  });
}

/** A completed run by some player, for the admin oversight view. */
export interface PlayerRun {
  id: string;
  player: { email: string; displayName: string | null } | null;
  mode: string;
  endingId: string | null;
  scores: Record<string, number> | null;
  startedAt: string;
  finishedAt: string | null;
}

export function usePlayerRuns(enabled: boolean) {
  return useQuery({
    queryKey: ["admin", "runs"],
    queryFn: () => apiFetch<{ runs: PlayerRun[] }>("/api/admin/runs").then((r) => r.runs),
    enabled,
  });
}

/** A completed run in full, including the transcript — for the admin's answer-by-answer review. */
export interface PlayerRunDetail extends PlayerRun {
  seed: number | null;
  transcript: unknown;
}

export function usePlayerRun(id: string | null) {
  return useQuery({
    queryKey: ["admin", "runs", id],
    enabled: !!id,
    queryFn: () => apiFetch<{ run: PlayerRunDetail }>(`/api/admin/runs/${id}`).then((r) => r.run),
  });
}
